import { createSignal, createEffect, on, onCleanup } from 'solid-js';
import { createContext, consume, type ICustomElement } from 'component-register';
import { computePosition, autoUpdate, offset as offsetMiddleware, flip, shift, type Placement } from '@floating-ui/dom';
import { animate, prefersReducedMotion, type AnimationPlaybackControls } from 'motion';

export interface ContextProps {
  placement: Placement;
  offset: number;
  open: boolean;
}

export interface ContextState extends ContextProps {
  element: HTMLElement & ICustomElement;
}

export interface TooltipStateAPI {
  isOpen: () => boolean;
  getContentId: () => string;
}

export interface TooltipActions {
  registerTrigger: (wrapper: HTMLElement, focusable: HTMLElement) => void;
  registerContent: (content: HTMLElement) => void;
  show: (opts?: { delay?: number }) => void;
  hide: () => void;
  toggle: () => void;
}

export type TooltipContextValue = [TooltipStateAPI, TooltipActions];

let nextId = 0;
const newId = () => `tooltip-${++nextId}`;

const OPEN_DURATION = 0.12;
const CLOSE_DURATION = 0.1;
const SHIFT_PADDING = 8;

export const TooltipContext = createContext((state: ContextState): TooltipContextValue => {
  const [triggerWrapper, setTriggerWrapper] = createSignal<HTMLElement | null>(null);
  const [focusableTrigger, setFocusableTrigger] = createSignal<HTMLElement | null>(null);
  const [content, setContent] = createSignal<HTMLElement | null>(null);
  const [open, setOpen] = createSignal(false);

  const contentId = newId();
  let openTimer: number | null = null;
  let activeAnimation: AnimationPlaybackControls | null = null;
  let stopAutoUpdate: (() => void) | null = null;

  const clearOpenTimer = () => {
    if (openTimer !== null) {
      window.clearTimeout(openTimer);
      openTimer = null;
    }
  };

  const isLocked = () => state.open;

  createEffect(
    on(
      () => state.open,
      (next, prev) => {
        if (next === prev) return;
        clearOpenTimer();
        setOpen(next);
      },
    ),
  );

  createEffect(() => {
    const f = focusableTrigger();
    const c = content();
    if (!f || !c) return;
    if (!c.id) c.id = contentId;
    f.setAttribute('aria-describedby', c.id);
    onCleanup(() => f.removeAttribute('aria-describedby'));
  });

  const openTooltip = (trigger: HTMLElement, contentEl: HTMLElement) => {
    try {
      if (!contentEl.matches(':popover-open')) contentEl.showPopover();
    } catch {
      // popover API unsupported — tooltip will still appear but won't escape clipping
    }
    stopAutoUpdate?.();
    stopAutoUpdate = autoUpdate(trigger, contentEl, () => {
      void computePosition(trigger, contentEl, {
        strategy: 'fixed',
        placement: state.placement,
        middleware: [offsetMiddleware(state.offset), flip(), shift({ padding: SHIFT_PADDING })],
      }).then(({ x, y }) => {
        Object.assign(contentEl.style, { position: 'fixed', left: `${x}px`, top: `${y}px` });
      });
    });

    activeAnimation?.cancel();
    if (!prefersReducedMotion.current) {
      activeAnimation = animate(contentEl, { opacity: 1 }, { duration: OPEN_DURATION, ease: 'easeOut' });
    } else {
      contentEl.style.opacity = '1';
    }
  };

  const closeTooltip = (contentEl: HTMLElement) => {
    stopAutoUpdate?.();
    stopAutoUpdate = null;
    activeAnimation?.cancel();

    const finish = () => {
      try {
        if (contentEl.matches(':popover-open')) contentEl.hidePopover();
      } catch {
        // no-op
      }
    };

    if (!prefersReducedMotion.current) {
      activeAnimation = animate(contentEl, { opacity: 0 }, { duration: CLOSE_DURATION, ease: 'easeIn' });
      activeAnimation.finished.then(finish).catch(() => {});
    } else {
      contentEl.style.opacity = '0';
      finish();
    }
  };

  createEffect(
    on(
      () => [open(), focusableTrigger(), content()] as const,
      ([isOpen, trigger, contentEl]) => {
        if (!trigger || !contentEl) return;
        if (isOpen) openTooltip(trigger, contentEl);
        else closeTooltip(contentEl);
      },
    ),
  );

  onCleanup(() => {
    clearOpenTimer();
    stopAutoUpdate?.();
    activeAnimation?.cancel();
  });

  return [
    {
      isOpen: () => open(),
      getContentId: () => contentId,
    },
    {
      registerTrigger: (wrapper, focusable) => {
        const existing = triggerWrapper();
        if (existing && existing !== wrapper) {
          console.warn('tooltip-context: only one <tooltip-trigger> is supported per <tooltip-context>');
          return;
        }
        setTriggerWrapper(wrapper);
        setFocusableTrigger(focusable);
      },
      registerContent: (el) => {
        const existing = content();
        if (existing && existing !== el) {
          console.warn('tooltip-context: only one <tooltip-content> is supported per <tooltip-context>');
          return;
        }
        el.setAttribute('popover', 'auto');
        el.setAttribute('role', 'tooltip');
        if (!el.id) el.id = contentId;
        el.addEventListener('toggle', (event) => {
          const e = event as ToggleEvent;
          if (e.newState === 'closed' && open() && !isLocked()) setOpen(false);
        });
        setContent(el);
      },
      show: (opts) => {
        clearOpenTimer();
        const delay = opts?.delay ?? 0;
        if (delay > 0) {
          openTimer = window.setTimeout(() => {
            openTimer = null;
            setOpen(true);
          }, delay);
        } else {
          setOpen(true);
        }
      },
      hide: () => {
        clearOpenTimer();
        if (isLocked()) return;
        setOpen(false);
      },
      toggle: () => {
        clearOpenTimer();
        if (isLocked()) return;
        setOpen((v) => !v);
      },
    },
  ];
});

export const useTooltip = (element: HTMLElement & ICustomElement): TooltipContextValue => {
  const ctx = consume(TooltipContext, element);
  if (!ctx) throw new Error('tooltip: must be inside <tooltip-context>');
  return ctx;
};
